/**
 * WithYouSettings
 *
 * Preferences UI for the "With You" proactive companion feature.
 * Lets the user choose cadence preset (gentle/regular/frequent/custom),
 * a custom interval if applicable, daily cap, quiet hours, timezone,
 * and the voice-default toggle.
 *
 * Supports two modes:
 * - Standalone (default): renders its own page layout with header
 * - Embedded (embedded=true): the shared SettingGroup layout, so it matches
 *   the rest of Settings rather than bringing its own look
 */

import React, { useEffect, useState } from 'react'
import { useWithYou } from '../../hooks/useWithYou'
import { fetchWithYouCadence, type WithYouCadence, type WithYouCadenceMode, type WithYouPreferences } from '../../services/withYouService'
import { SettingGroup } from '../settings/SettingRow'
import { Button } from '../ui/Button'
import { Bell, BellOff, Globe, CheckCircle, AlertCircle, Save } from 'lucide-react'
import { Link } from 'react-router-dom'
import { BRAND } from '../../config/brand'

const CADENCE_OPTIONS: { value: WithYouCadenceMode; label: string; description: string; icon: string }[] = [
  { value: 'off', label: 'Off', description: 'No proactive check-ins. You can still open With You yourself.', icon: '🔇' },
  { value: 'gentle', label: 'Gentle', description: 'A few times a day (about every 6 hours).', icon: '🌿' },
  { value: 'regular', label: 'Regular', description: 'Every couple of hours.', icon: '☀️' },
  { value: 'frequent', label: 'Frequent', description: 'About once an hour.', icon: '💛' },
  { value: 'custom', label: 'Custom', description: 'Pick your own interval.', icon: '⚙️' },
]

const CUSTOM_INTERVAL_STOPS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 240, label: '4 hours' },
  { value: 480, label: '8 hours' },
  { value: 720, label: '12 hours' },
  { value: 1440, label: '24 hours' },
]


interface WithYouSettingsProps {
  embedded?: boolean
}

export const WithYouSettings: React.FC<WithYouSettingsProps> = ({ embedded = false }) => {
  const { preferences, loadingPrefs, loadPreferences, updatePreferences, error } = useWithYou()
  const [draft, setDraft] = useState<Partial<WithYouPreferences>>({})
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    void loadPreferences()
  }, [loadPreferences])

  useEffect(() => {
    if (preferences) setDraft(preferences)
  }, [preferences])

  // What the cadence knows right now (2026-09-15): the window notes land in,
  // from the rhythm or the profile's day shape, and the two holding rules.
  const [cadence, setCadence] = useState<WithYouCadence | null>(null)
  useEffect(() => {
    let cancelled = false
    fetchWithYouCadence().then(c => { if (!cancelled) setCadence(c) }).catch(() => undefined)
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (saveMessage) {
      const t = setTimeout(() => setSaveMessage(null), 3000)
      return () => clearTimeout(t)
    }
  }, [saveMessage])

  if (loadingPrefs && !preferences) {
    return (
      <div className="flex items-center justify-center p-12 text-muted">
        <div className="animate-spin h-5 w-5 border-2 border-brand border-t-transparent rounded-full mr-3" />
        Loading preferences...
      </div>
    )
  }

  const merged: Partial<WithYouPreferences> = { ...preferences, ...draft }

  const set = <K extends keyof WithYouPreferences>(key: K, value: WithYouPreferences[K]) => {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await updatePreferences(draft)
      setSaveMessage({ type: 'success', text: 'With You preferences saved' })
    } catch (e) {
      setSaveMessage({
        type: 'error',
        text: e instanceof Error ? e.message : 'Could not save that',
      })
    } finally {
      setSaving(false)
    }
  }

  const isEnabled = merged.cadence_mode !== 'off'

  const content = (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-bad/30 bg-bad/10 p-3 text-sm text-bad flex items-center gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Cadence */}
      <SettingGroup title="Check-in frequency" blurb="How often it reaches out during the day.">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {CADENCE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => set('cadence_mode', opt.value)}
                className={`text-left p-3 rounded-lg border-2 transition-all ${
                  merged.cadence_mode === opt.value
                    ? 'border-brand bg-brand-subtle shadow-sm'
                    : 'border-soft hover:border-soft'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{opt.icon}</span>
                  <span className="font-medium text-primary text-sm">{opt.label}</span>
                </div>
                <div className="text-xs text-muted">{opt.description}</div>
              </button>
            ))}
          </div>

          {cadence && isEnabled && (
            <div className="mt-3 rounded-lg bg-surface-alt p-3 text-sm text-secondary space-y-1" data-testid="with-you-cadence-words">
              {cadence.words.map(w => <p key={w}>{w}</p>)}
              <p className="text-app-micro text-muted">However often you pick above, a note waits for that window. <Link to="/analytics" className="text-brand hover:underline">See your rhythm</Link></p>
            </div>
          )}

          {merged.cadence_mode === 'custom' && (
            <div className="mt-3 p-3 bg-surface-alt rounded-lg">
              <label className="block text-sm font-medium text-secondary mb-2">
                Custom interval
              </label>
              <select
                value={merged.cadence_interval_minutes ?? 360}
                onChange={(e) => set('cadence_interval_minutes', Number(e.target.value))}
                className="w-full sm:w-64 rounded-lg border border-soft bg-surface p-2 text-sm"
              >
                {CUSTOM_INTERVAL_STOPS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}
      </SettingGroup>

      {isEnabled && (
        <>
          {/* Quiet hours + Timezone */}
      <SettingGroup title="Quiet hours" blurb="No check-ins during these hours, in your local time.">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">From</label>
                  <input
                    type="time"
                    value={merged.quiet_hours_start ?? '22:00'}
                    onChange={(e) => set('quiet_hours_start', e.target.value)}
                    className="rounded-lg border border-soft bg-surface p-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">To</label>
                  <input
                    type="time"
                    value={merged.quiet_hours_end ?? '07:00'}
                    onChange={(e) => set('quiet_hours_end', e.target.value)}
                    className="rounded-lg border border-soft bg-surface p-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">
                  <Globe className="h-4 w-4 inline mr-1" />
                  Your timezone
                </label>
                {/* One fact, one place (2026-09-15): the profile owns the timezone.
                    This used to be a second picker, defaulted to Asia/Kolkata, and
                    quiet hours were checked against it. */}
                <p className="text-sm text-primary">
                  {merged.user_timezone ?? 'UTC'}
                  <span className="text-app-micro text-muted"> · from your profile</span>
                </p>
                <Link to="/profile" className="text-app-micro text-brand hover:underline">Change it in your profile</Link>
              </div>
      </SettingGroup>

          {/* Daily cap & notifications */}
      <SettingGroup title="How many, and how" blurb="A ceiling on check-ins, and how they reach you.">
              <div className="flex flex-wrap gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Max check-ins per day
                  </label>
              <p className="text-app-micro text-muted mb-1">However often you picked above, it never exceeds this in one day.</p>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    value={merged.max_pushes_per_day ?? 3}
                    onChange={(e) => set('max_pushes_per_day', Number(e.target.value))}
                    className="w-24 rounded-lg border border-soft bg-surface p-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Minimum spacing (minutes)
                  </label>
              <p className="text-app-micro text-muted mb-1">The least time that must pass between two check-ins.</p>
                  <input
                    type="number"
                    min={15}
                    max={1440}
                    step={5}
                    value={merged.minimum_spacing_minutes ?? 60}
                    onChange={(e) => set('minimum_spacing_minutes', Number(e.target.value))}
                    className="w-24 rounded-lg border border-soft bg-surface p-2 text-sm"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 p-3 rounded-lg border border-soft cursor-pointer hover:bg-surface-alt transition-colors">
                <input
                  type="checkbox"
                  checked={merged.allow_push_notifications !== false}
                  onChange={(e) => set('allow_push_notifications', e.target.checked)}
                  className="h-4 w-4 rounded border-soft text-brand focus:ring-brand"
                />
                <div>
                  <div className="text-sm font-medium text-primary flex items-center gap-1.5">
                    {merged.allow_push_notifications !== false ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                    Push notifications
                  </div>
                  <div className="text-xs text-muted">
                    Receive check-in reminders as push notifications on your phone
                  </div>
                </div>
              </label>
      </SettingGroup>

          {/* Voice */}
      <SettingGroup title="Voice" blurb="Whether check-ins are spoken aloud by default.">
              <label className="flex items-center gap-3 p-3 rounded-lg border border-soft cursor-pointer hover:bg-surface-alt transition-colors">
                <input
                  type="checkbox"
                  checked={!!merged.allow_voice_default}
                  onChange={(e) => set('allow_voice_default', e.target.checked)}
                  className="h-4 w-4 rounded border-soft text-brand focus:ring-brand"
                />
                <div>
                  <div className="text-sm font-medium text-primary">Show voice button by default</div>
                  <div className="text-xs text-muted">
                    Display the voice recording option in With You check-ins
                  </div>
                </div>
              </label>
      </SettingGroup>
        </>
      )}

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-brand hover:bg-brand-hover text-on-brand"
        >
          {saving ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save preferences
            </>
          )}
        </Button>
      </div>

      {/* Inline save toast for embedded mode */}
      {saveMessage && embedded && (
        <div className={`rounded-lg p-3 text-sm flex items-center gap-2 ${
          saveMessage.type === 'success'
            ? 'bg-ok/10 text-ok border border-ok/30'
            : 'bg-bad/10 text-bad border border-bad/30'
        }`}>
          {saveMessage.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {saveMessage.text}
        </div>
      )}

      {/* Floating toast for standalone mode */}
      {saveMessage && !embedded && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div className={`p-4 rounded-xl shadow-2xl flex items-center gap-3 w-full sm:min-w-[300px] sm:max-w-[450px] ${
            saveMessage.type === 'success' ? 'bg-ok text-on-brand' : 'bg-bad text-on-brand'
          }`}>
            {saveMessage.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            <span className="text-sm">{saveMessage.text}</span>
          </div>
        </div>
      )}
    </div>
  )

  if (embedded) return content

  return (
    <div className="max-w-3xl mx-auto p-6">
      <header className="mb-6">
        <h1 className="text-app-display font-semibold text-primary">With You</h1>
        <p className="text-sm text-secondary mt-1">
          {BRAND.assistant} can quietly check in on you throughout the day. You stay in full control of how
          often, when, and whether at all.
        </p>
      </header>
      {content}
    </div>
  )
}

export default WithYouSettings
